import { fetchAllPages } from './crm/CrmApiRepository.js';
import { ActivitiesApiService } from './crm/ActivitiesApiService.js';
import { mapActivityFromApi, mapActivityToApi } from './crm/ApiEntityMapper.js';
import { generateId, nowIso } from '../utils/IdUtils.js';
import { auth } from './AuthService.js';

export class ActivityService {
    static async getForEntity(entityType, entityId, { page = 1, perPage = 50, sortOrder = 'desc', source = null, isInternal = null, activityType = null } = {}) {
        const params = { page, per_page: perPage, sort_order: sortOrder };
        if (source) params.source = source;
        if (isInternal !== null) params.is_internal = isInternal;
        if (activityType) params.activity_type = activityType;
        const rows = await ActivitiesApiService.getForEntity(entityType, entityId, params);
        return rows
            .map(mapActivityFromApi)
            .sort((a, b) => {
                const order = sortOrder === 'asc' ? 1 : -1;
                return (b.createdAt || '').localeCompare(a.createdAt || '') * order;
            });
    }

    static async listRecent({ perPage = 50, activityType = null, isInternal = null } = {}) {
        const params = { page: 1, per_page: perPage, sort_order: 'desc' };
        if (activityType) params.activity_type = activityType;
        if (isInternal !== null) params.is_internal = isInternal;
        const rows = await ActivitiesApiService.list(params);
        const list = Array.isArray(rows) ? rows : (rows?.data ?? []);
        return list.map(mapActivityFromApi);
    }

    static async getAllForEntity(entityType, entityId, { sortOrder = 'desc', source = null } = {}) {
        const params = { sort_order: sortOrder };
        if (source) params.source = source;
        const rows = await ActivitiesApiService.getAllForEntity(entityType, entityId, params);
        return rows
            .map(mapActivityFromApi)
            .sort((a, b) => {
                const order = sortOrder === 'asc' ? 1 : -1;
                return (b.createdAt || '').localeCompare(a.createdAt || '') * order;
            });
    }

    static async addNote(entityType, entityId, body, subject = 'Note', mentions = [], isInternal = false) {
        return ActivityService._add({
            entityType,
            entityId,
            type: 'note',
            subject,
            body,
            dueDate: null,
            completed: true,
            mentions,
            isInternal
        });
    }

    static async addComment(entityType, entityId, body, mentions = [], isInternal = false) {
        return ActivityService._add({
            entityType,
            entityId,
            type: 'comment',
            subject: 'Comment',
            body,
            dueDate: null,
            completed: true,
            mentions,
            isInternal
        });
    }

    static async addTask(entityType, entityId, subject, dueDate, body = '', assignedTo = '') {
        const mentions = assignedTo ? [assignedTo] : [];
        return ActivityService._add({
            entityType,
            entityId,
            type: 'task',
            subject,
            body,
            dueDate,
            completed: false,
            mentions
        });
    }

    static async logEmail(entityType, entityId, subject, body) {
        return ActivityService._add({
            entityType,
            entityId,
            type: 'email',
            subject,
            body,
            dueDate: null,
            completed: true
        });
    }

    static async _add(partial) {
        const session = auth.getSession();
        const payload = mapActivityToApi({
            ...partial,
            dealId: partial.entityType === 'deal' ? partial.entityId : null,
            clientId: partial.entityType === 'client' ? partial.entityId : null,
            mentions: partial.mentions || []
        });
        const saved = await ActivitiesApiService.create(payload);
        return {
            id: saved?.id || generateId('act'),
            ...mapActivityFromApi(saved),
            createdBy: session?.userId,
            createdAt: saved?.created_at || nowIso(),
            updatedAt: saved?.updated_at || nowIso()
        };
    }

    static async toggleTask(id, completed) {
        const saved = await ActivitiesApiService.update(id, {
            is_completed: completed ? 'true' : 'false'
        });
        return {
            id: saved?.id || id,
            completed,
            updatedAt: saved?.updated_at || nowIso()
        };
    }

    static async updateActivity(id, updates) {
        const payload = { ...updates };
        if (payload.dueDate !== undefined && payload.due_date === undefined) {
            payload.due_date = payload.dueDate || null;
            delete payload.dueDate;
        }
        const saved = await ActivitiesApiService.update(id, payload);
        return {
            id: saved?.id || id,
            ...mapActivityFromApi(saved),
            updatedAt: saved?.updated_at || nowIso()
        };
    }

    static async deleteTask(id) {
        await ActivitiesApiService.remove(id);
        return { id };
    }

    static async deleteActivity(id) {
        await ActivitiesApiService.remove(id);
        return { id };
    }
}
